import type React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import { updateLegalActStatus } from "../../../lib/api/api"
import { formatDate } from "../../../shared/utils/format"

export type LegalStatus = "in_vigore" | "abrogato" | "sconosciuto"

// Legal-status fields returned by the legal-acts API
export type LegalStatusFields = {
  legal_status?: LegalStatus | null
  legal_status_source?: string | null
  legal_status_reference?: string | null
  legal_status_url?: string | null
  legal_status_checked_at?: string | null
  text_version_date?: string | null
}

const toStatus = (value?: string | null): LegalStatus =>
  value === "in_vigore" || value === "abrogato" ? value : "sconosciuto"

const isHttpUrl = (url?: string | null): url is string => !!url && /^https?:\/\//i.test(url)

type LegalStatusBadgeProps = LegalStatusFields & {
  /** The act's id: required to save a change (only used when canEdit is true). */
  actId?: number | string
  canEdit?: boolean
  /** Called after a successful save, with the fields the server returned. */
  onUpdated?: (fields: LegalStatusFields) => void
  /** Called when the save fails; the badge itself never shows the error. */
  onError?: (error: unknown) => void
  className?: string
}

/**
 * Small "In vigore" / "Abrogato" badge shown next to a law's title.
 * Unverified acts show nothing to regular users; the super admin (canEdit)
 * gets a compact select (styled as the badge) that saves through
 * `updateLegalActStatus`.
 */
export default function LegalStatusBadge({
  actId,
  canEdit = false,
  onUpdated,
  onError,
  className = "",
  ...fields
}: LegalStatusBadgeProps) {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const status = toStatus(fields.legal_status)

  const LABELS: Record<LegalStatus, string> = {
    in_vigore: t("legalStatus.in_vigore"),
    abrogato: t("legalStatus.abrogato"),
    sconosciuto: t("legalStatus.sconosciuto"),
  }

  const SOURCE_LABELS: Record<string, string> = {
    lexfind: t("legalStatus.sources.lexfind"),
    rl: t("legalStatus.sources.rl"),
    fedlex: t("legalStatus.sources.fedlex"),
    manuale: t("legalStatus.sources.manuale"),
  }

  const COLORS: Record<LegalStatus, string> = {
    in_vigore: "bg-green-100 text-green-800 border-green-200",
    abrogato: "bg-red-100 text-red-800 border-red-200",
    sconosciuto: "bg-gray-100 text-gray-600 border-gray-200"
  }

  const buildTooltip = (): string => {
    const parts: string[] = [LABELS[status]]
    if (fields.legal_status_reference) parts.push(fields.legal_status_reference)
    if (fields.legal_status_source) {
      parts.push(SOURCE_LABELS[fields.legal_status_source] || fields.legal_status_source)
    }
    if (fields.legal_status_checked_at) {
      const date = formatDate(fields.legal_status_checked_at)
      if (date) parts.push(t("legalStatus.checkedOn", { date }))
    }
    if (fields.text_version_date) {
      const date = formatDate(fields.text_version_date)
      if (date) parts.push(t("legalStatus.textVersionFrom", { date }))
    }
    return parts.join(" · ")
  }

  const tooltip = buildTooltip()

  const reference = status !== "sconosciuto" && fields.legal_status_reference ? (
    isHttpUrl(fields.legal_status_url) ? (
      <a
        href={fields.legal_status_url}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 text-xs text-gray-500 hover:text-primary-600 hover:underline whitespace-nowrap"
        title={t("legalStatus.openOfficialText")}
      >
        {fields.legal_status_reference}
      </a>
    ) : (
      <span className="ml-1 text-xs text-gray-500 whitespace-nowrap">{fields.legal_status_reference}</span>
    )
  ) : null

  if (canEdit) {
    const handleSelect = async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = toStatus(event.target.value)
      if (next === status || actId == null) return
      setIsSaving(true)
      try {
        const keepReference = next !== "sconosciuto"
        const response = await updateLegalActStatus(actId, {
          legal_status: next,
          legal_status_reference: keepReference ? fields.legal_status_reference ?? null : null,
          legal_status_url: keepReference ? fields.legal_status_url ?? null : null,
        })
        onUpdated?.(response.data)
      } catch (error) {
        onError?.(error)
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <span className={`inline-flex items-center flex-shrink-0 ${className}`}>
        <select
          aria-label={t("legalStatus.ariaLabel")}
          title={tooltip}
          value={status}
          disabled={isSaving}
          onChange={handleSelect}
          onClick={(event) => event.stopPropagation()}
          className={`text-xs font-medium border rounded-full px-2 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 ${COLORS[status]}`}
        >
          <option value="in_vigore">{LABELS.in_vigore}</option>
          <option value="abrogato">{LABELS.abrogato}</option>
          <option value="sconosciuto">{LABELS.sconosciuto}</option>
        </select>
        {isSaving && <Loader2 className="ml-1 h-3 w-3 animate-spin text-gray-500" />}
        {reference}
      </span>
    )
  }

  if (status === "sconosciuto") return null

  return (
    <span className={`inline-flex items-center flex-shrink-0 ${className}`}>
      <span
        className={`text-xs font-medium border rounded-full px-2 py-0.5 ${COLORS[status]}`}
        title={tooltip}
      >
        {LABELS[status]}
      </span>
      {reference}
    </span>
  )
}
